<template>
    <a-modal
            :footer="null"
            width="500px"
            wrap-class-name="custom_pay_modal"
            :visible="recharge"
            :maskClosable="false"
            @cancel="close"
    >
        <div class="custom_pay">
            <div class="custom_pay_header">
                <span>定制费用支付</span>
                <p>请确认与客服约定的定制标书费用</p>
            </div>
            <div class="custom_pay_body">
                <template v-if="!paid">
                    <template v-if="!orderId">
                        <div class="custom_pay_heading">
                            <label for="customPayAmount">付款金额</label>
                        </div>
                        <div class="custom_pay_input" :class="{custom_pay_input_error: error}">
                            <a-input
                                    id="customPayAmount"
                                    ref="amountInput"
                                    :value="amount"
                                    prefix="¥"
                                    suffix="元"
                                    size="large"
                                    inputmode="numeric"
                                    autocomplete="off"
                                    placeholder="请输入整数金额"
                                    :disabled="loading"
                                    :aria-invalid="!!error"
                                    aria-describedby="customPayAmountHelp"
                                    @input="handleAmountInput"
                                    @blur="validateAmount"
                                    @pressEnter="createOrder"
                            />
                        </div>
                        <div id="customPayAmountHelp" class="custom_pay_help" :class="{custom_pay_error: error}" aria-live="polite">
                            <a-icon v-if="error" type="info-circle" />
                            {{ error || '支持 1～100000 元的整数金额' }}
                        </div>
                        <a-button class="custom_pay_submit" type="primary" size="large" block :loading="loading" :disabled="!canPay || loading" @click="createOrder">
                            立即支付
                        </a-button>
                        <div class="custom_pay_methods">
                            <span><img src="@/assets/image/img/weixinzhifu.svg" alt="">微信支付</span>
                            <span><img src="@/assets/image/img/zhifubaozhifu.svg" alt="">支付宝支付</span>
                        </div>
                    </template>
                    <div v-else class="custom_pay_code">
                        <div class="custom_pay_heading">
                            <span>扫码支付</span>
                            <a-button type="link" @click="editAmount">修改金额</a-button>
                        </div>
                        <div class="custom_pay_scan">
                            <div class="custom_pay_qr">
                                <div ref="qrCodeImg" role="img" :aria-label="`定制标书费用 ${payAmount} 元付款二维码`"></div>
                            </div>
                            <div class="custom_pay_summary">
                                <span class="custom_pay_category">定制费</span>
                                <div class="custom_pay_amount"><small>¥</small> {{ payAmount }}<small> 元</small></div>
                                <div class="custom_pay_methods">
                                    <span><img src="@/assets/image/img/weixinzhifu.svg" alt="">微信支付</span>
                                    <span><img src="@/assets/image/img/zhifubaozhifu.svg" alt="">支付宝支付</span>
                                </div>
                                <div class="custom_pay_tip">请使用微信或支付宝扫码</div>
                            </div>
                        </div>
                        <div class="custom_pay_order">订单号：{{ serialNumber }}</div>
                        <div v-if="socketUnavailable" class="custom_pay_help" role="status">支付结果通知暂不可用，可在个人中心查看订单。</div>
                    </div>
                    <div class="custom_pay_note">
                        <span>支付后可在个人中心的<span class="custom_pay_record">充值记录</span>中查看“定制费”订单。</span>
                    </div>
                </template>
                <div v-else class="custom_pay_success" role="status">
                    <a-icon type="check-circle" theme="filled" />
                    <div class="custom_pay_success_title">定制费用支付成功</div>
                    <div class="custom_pay_amount"><small>¥</small> {{ payAmount }}</div>
                    <p class="custom_pay_tip">可在个人中心的充值记录中查看本次付款</p>
                    <a-button class="custom_pay_submit" type="primary" size="large" block @click="close">完成</a-button>
                </div>
            </div>
        </div>
    </a-modal>
</template>

<script>
import QRCode from 'qrcodejs2';
import {createCustomOrder} from '@/api/page/pay';
import {decrypt, encrypt} from '@/utils/AES';
import {paymentSocketUrl} from '@/utils/websocket';
import {getRandomCode} from '@/utils/RandomCode';

export default {
    name: 'customPay',
    props: {
        recharge: Boolean,
        initialAmount: {type: String, default: ''},
        autoCreate: {type: Boolean, default: false},
        paymentUser: {type: Object, default: null},
        orderCreator: {type: Function, default: null}
    },
    data() {
        return {
            amount: this.initialAmount,
            payAmount: '',
            error: '',
            loading: false,
            paid: false,
            orderId: null,
            serialNumber: '',
            socketUnavailable: false
        }
    },
    computed: {
        canPay() {
            const value = String(this.amount).trim()
            return /^\d{1,6}$/.test(value) && Number(value) >= 1 && Number(value) <= 100000
        }
    },
    created() {
        this.paymentSocket = null
        this.heartbeat = null
        this.reconnectTimer = null
        this.reconnectCount = 0
        this.closed = false
    },
    beforeDestroy() {
        this.closed = true
        this.closeSocket()
    },
    mounted() {
        if (this.autoCreate) this.createOrder()
        else this.$nextTick(() => this.$refs.amountInput && this.$refs.amountInput.focus())
    },
    methods: {
        handleAmountInput(event) {
            const value = String(event && event.target ? event.target.value : event).trim()
            if (!/^\d{0,6}$/.test(value) || Number(value) > 100000) {
                this.error = '仅支持 1～100000 元的整数，请重新输入'
                if (event && event.target) {
                    event.target.value = this.amount
                }
                return
            }
            this.amount = value
            if (event && event.target) {
                event.target.value = value
            }
            this.error = ''
        },
        validateAmount() {
            const value = String(this.amount).trim()
            const valid = this.canPay
            this.error = valid ? '' : '请输入 1～100000 元的整数金额'
            if (valid) {
                this.amount = String(Number(value))
            }
            return valid
        },
        async createOrder() {
            if (this.loading || this.orderId) {
                return
            }
            if (!this.validateAmount()) {
                this.$nextTick(() => this.$refs.amountInput && this.$refs.amountInput.focus())
                return
            }
            this.loading = true
            try {
                const userInfo = this.paymentUser || JSON.parse(decrypt(localStorage.getItem('userInfo')))
                const payAmount = String(Number(this.amount))
                const serialNumber = 'BXZPC' + Date.now() + Math.random().toString(36).slice(2, 8).toUpperCase()
                const orderId = await (this.orderCreator || createCustomOrder)({
                    payAmount,
                    serialNumber,
                    promoteId: localStorage.getItem('promoted_id') || null,
                    payUrl: localStorage.getItem('promoteUrl') || '',
                    code: getRandomCode()
                })
                if (this.closed) {
                    return
                }
                this.orderId = orderId
                this.payAmount = payAmount
                this.serialNumber = serialNumber
                const query = new URLSearchParams({
                    amount: payAmount,
                    title: '定制标书费用',
                    taxNum: serialNumber,
                    orderId: String(orderId),
                    userId: String(userInfo.id),
                    orderType: '3'
                })
                await this.$nextTick()
                if (this.closed) {
                    return
                }
                new QRCode(this.$refs.qrCodeImg, {
                    text: `${process.env.VUE_APP_PAY_URL}?${query.toString()}`,
                    width: 180,
                    height: 180,
                    correctLevel: QRCode.CorrectLevel.H
                })
                this.connectSocket()
            } catch (err) {
                if (!this.closed) {
                    this.orderId = null
                    this.error = err.message || '创建付款订单失败，请稍后重试'
                }
            } finally {
                this.loading = false
            }
        },
        connectSocket() {
            if (this.closed || this.paid) {
                return
            }
            try {
                const socket = new WebSocket(paymentSocketUrl)
                this.paymentSocket = socket
                socket.onopen = () => {
                    this.socketUnavailable = false
                    socket.send(encrypt(JSON.stringify({orderId: this.orderId})))
                    this.heartbeat = window.setInterval(() => {
                        if (socket.readyState === WebSocket.OPEN) {
                            socket.send('ping')
                        }
                    }, 3000)
                }
                socket.onmessage = e => {
                    if (typeof e.data === 'string' && e.data.includes(`serialNumber:${this.serialNumber}支付状态:`) && e.data.endsWith(',success')) {
                        this.paid = true
                        this.closeSocket()
                        this.$bus.$emit('customPaySuccess', true)
                    }
                }
                socket.onerror = () => {
                    this.socketUnavailable = true
                }
                socket.onclose = () => {
                    window.clearInterval(this.heartbeat)
                    this.socketUnavailable = true
                    if (!this.closed && !this.paid && this.reconnectCount < 3) {
                        this.reconnectCount += 1
                        this.reconnectTimer = window.setTimeout(() => this.connectSocket(), 2000)
                    }
                }
            } catch (err) {
                this.socketUnavailable = true
            }
        },
        closeSocket() {
            window.clearInterval(this.heartbeat)
            window.clearTimeout(this.reconnectTimer)
            if (this.paymentSocket) {
                this.paymentSocket.onopen = null
                this.paymentSocket.onmessage = null
                this.paymentSocket.onclose = null
                this.paymentSocket.onerror = null
                this.paymentSocket.close()
                this.paymentSocket = null
            }
        },
        editAmount() {
            this.closeSocket()
            this.orderId = null
            this.error = ''
            this.reconnectCount = 0
            this.socketUnavailable = false
            this.$nextTick(() => this.$refs.amountInput && this.$refs.amountInput.focus())
        },
        close() {
            this.closed = true
            this.closeSocket()
            this.$emit('payClose', true)
        }
    }
}
</script>

<style scoped lang="scss">
.custom_pay {
    width: 500px;
    max-width: 100%;
    box-sizing: border-box;
    color: #333333;
    .custom_pay_header {
        padding: 20px 24px 16px;
        border-bottom: 1px solid #f0f1f3;
        background: #ffffff;
        font-size: 18px;
        line-height: 26px;
        font-weight: 600;
        p { margin: 5px 0 0; color: #737373; font-size: 12px; line-height: 20px; font-weight: 400; }
    }
    .custom_pay_body { padding: 20px 24px; }
    .custom_pay_heading { display: flex; align-items: center; justify-content: space-between; font-size: 14px; line-height: 22px; font-weight: 500; }
    .custom_pay_heading .ant-btn { height: auto; padding: 0; color: #2f6fdb; }
    .custom_pay_heading .ant-btn:focus-visible { outline: 2px solid #2f6fdb; outline-offset: 4px; border-radius: 2px; }
    .custom_pay_tip { margin: 8px 0 20px; color: #737373; font-size: 13px; }
    .custom_pay_input {
        margin-top: 10px;
        ::v-deep .ant-input { height: 58px; padding-left: 44px; padding-right: 40px; border-color: #dcdfe5; border-radius: 8px; background: #ffffff; color: #262626; font-size: 28px; font-weight: 500; font-variant-numeric: tabular-nums; }
        ::v-deep .ant-input::placeholder { color: #a1a7b3; font-size: 19px; font-weight: 400; }
        ::v-deep .ant-input-prefix { left: 18px; color: #595959; font-size: 22px; }
        ::v-deep .ant-input-suffix { right: 18px; color: #737373; font-size: 14px; }
        ::v-deep .ant-input:hover { border-color: #b2b7c2; }
        ::v-deep .ant-input:focus { border-color: #2f6fdb; box-shadow: 0 0 0 3px rgba(47, 111, 219, .12); }
        ::v-deep .ant-input:disabled { background: #fafafa; color: #737373; }
    }
    .custom_pay_input_error ::v-deep .ant-input { border-color: #cf1322; }
    .custom_pay_help { min-height: 36px; padding: 8px 0; color: #737373; font-size: 12px; line-height: 20px; }
    .custom_pay_help .anticon { margin-right: 4px; }
    .custom_pay_error { color: #cf1322; }
    .custom_pay_submit.ant-btn {
        height: 44px;
        border: 1px solid #2f6fdb;
        border-radius: 8px;
        background: #2f6fdb;
        box-shadow: 0 3px 8px rgba(47, 111, 219, .12);
        font-size: 15px;
        font-weight: 500;
        transition: background-color .15s, border-color .15s, box-shadow .15s, transform .15s;
    }
    .custom_pay_submit.ant-btn:hover { background: #245bc0; border-color: #245bc0; box-shadow: 0 5px 12px rgba(47, 111, 219, .2); transform: translateY(-1px); }
    .custom_pay_submit.ant-btn:focus-visible { outline: 2px solid #2f6fdb; outline-offset: 3px; }
    .custom_pay_submit.ant-btn:active { background: #1f50ad; border-color: #1f50ad; box-shadow: 0 2px 4px rgba(47, 111, 219, .12); transform: translateY(0); }
    .custom_pay_submit.ant-btn[disabled] { border-color: #bdd0ee; background: #bdd0ee; color: #526b91; box-shadow: none; transform: none; }
    .custom_pay_submit.ant-btn.ant-btn-loading { border-color: #2f6fdb; background: #2f6fdb; color: #ffffff; cursor: wait; box-shadow: none; transform: none; }
    .custom_pay_methods { display: flex; flex-wrap: wrap; justify-content: center; gap: 12px 24px; margin: 16px 0 0; color: #737373; font-size: 13px; }
    .custom_pay_methods span { display: inline-flex; align-items: center; gap: 6px; }
    .custom_pay_methods img { width: 18px; height: 18px; }
    .custom_pay_note { margin-top: 20px; padding-top: 14px; border-top: 1px solid #f0f1f3; color: #737373; font-size: 12px; line-height: 20px; overflow-wrap: anywhere; }
    .custom_pay_record { color: #595959; }
    .custom_pay_scan { display: flex; align-items: center; gap: 20px; margin-top: 18px; }
    .custom_pay_qr { flex-shrink: 0; padding: 10px; border: 1px solid #e9ebef; border-radius: 8px; background: #fff; }
    .custom_pay_summary { flex: 1; min-width: 0; }
    .custom_pay_category { display: inline-block; padding: 3px 9px; border-radius: 4px; background: #f5f6f8; color: #595959; font-size: 12px; }
    .custom_pay_amount { margin: 10px 0; color: #2f6fdb; font-size: 28px; font-weight: 600; font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }
    .custom_pay_amount small { font-size: 16px; font-weight: 400; }
    .custom_pay_summary .custom_pay_methods { flex-direction: column; align-items: flex-start; gap: 12px; margin-top: 16px; }
    .custom_pay_summary .custom_pay_tip { margin-bottom: 0; font-size: 12px; }
    .custom_pay_order { margin-top: 16px; color: #737373; font-size: 12px; overflow-wrap: anywhere; }
    .custom_pay_success {
        padding: 12px 0 4px;
        text-align: center;
        > .anticon { margin-bottom: 16px; color: #52c41a; font-size: 44px; }
        .custom_pay_success_title { font-size: 18px; font-weight: 500; }
        .custom_pay_tip { margin: 0 0 24px; }
    }
}
@media (max-width: 520px) {
    .custom_pay {
        .custom_pay_header { padding: 20px 20px 16px; }
        .custom_pay_body { padding: 20px; }
    }
}
@media (max-width: 440px) {
    .custom_pay {
        .custom_pay_scan { flex-direction: column; gap: 16px; }
        .custom_pay_summary { width: 100%; text-align: center; }
        .custom_pay_summary .custom_pay_methods { flex-direction: row; justify-content: center; }
    }
}
</style>

<style lang="scss">
.custom_pay_modal {
    .ant-modal { top: 80px; max-width: calc(100% - 32px); padding-bottom: 24px; }
    .ant-modal-content { width: 500px; max-width: 100%; border: 1px solid rgba(255, 255, 255, .85); border-radius: 14px; box-shadow: 0 20px 60px rgba(25, 33, 45, .18), 0 4px 16px rgba(25, 33, 45, .08); overflow: hidden; }
    .ant-modal-body { padding: 0; }
    .ant-modal-close { top: 14px; right: 12px; color: #737373; border-radius: 6px; transition: color .15s, background-color .15s; }
    .ant-modal-close-x { width: 36px; height: 36px; line-height: 36px; }
    .ant-modal-close:hover { background: #f5f6f8; color: #333333; }
    .ant-modal-close:focus-visible { outline: 2px solid #2f6fdb; outline-offset: 2px; }
}
@media (max-width: 520px), (max-height: 600px) {
    .custom_pay_modal {
        .ant-modal { top: 20px; margin: 0 auto; padding-bottom: 20px; }
        .ant-modal-content { max-height: calc(100vh - 40px); max-height: calc(100dvh - 40px); overflow-y: auto; overscroll-behavior: contain; }
    }
}
@media (prefers-reduced-motion: reduce) {
    .custom_pay .custom_pay_submit.ant-btn { transition: none; transform: none; }
    .custom_pay_modal .ant-modal-close { transition: none; }
}
</style>
